/**
 * 🎯 Smart Cache Manager
 * 
 * Özellikleri:
 * - Kullanıcı bazlı cache (username prefix)
 * - TTL (Time To Live) desteği
 * - LRU (Least Recently Used) stratejisi
 * - LocalStorage quota aşımı yönetimi
 * - Pattern bazlı cache temizleme
 */

class CacheManager {
  constructor() {
    this.prefix = 'tivigo_cache_';
  }

  /**
   * Mevcut kullanıcı adını al
   */
  getCurrentUsername() {
    try {
      const credentials = JSON.parse(localStorage.getItem('xtream_credentials') || '{}');
      return credentials.username || 'guest';
    } catch {
      return 'guest';
    }
  }

  /**
   * Cache key oluştur (username prefix ile)
   */
  buildKey(key, useUsername = true) {
    if (useUsername) {
      const username = this.getCurrentUsername();
      return `${this.prefix}${username}_${key}`;
    }
    return `${this.prefix}${key}`;
  }

  /**
   * Cache'e veri kaydet
   * @param {string} key - Cache anahtarı
   * @param {any} value - Kaydedilecek veri
   * @param {number} ttl - Time to live (ms) - varsayılan 24 saat
   * @param {boolean} useUsername - Username prefix kullan mı?
   */
  set(key, value, ttl = 24 * 60 * 60 * 1000, useUsername = true) {
    try {
      const fullKey = this.buildKey(key, useUsername);
      const expiresAt = Date.now() + ttl;
      
      const cacheData = {
        value,
        expiresAt,
        cachedAt: Date.now(),
        username: useUsername ? this.getCurrentUsername() : null
      };

      localStorage.setItem(fullKey, JSON.stringify(cacheData));
      localStorage.setItem(`${fullKey}_timestamp`, expiresAt.toString());
      
      return true;
    } catch (error) {
      console.warn('⚠️ Cache yazma hatası, eski cache temizleniyor...', error);
      this.cleanup(key);
      
      try {
        const fullKey = this.buildKey(key, useUsername);
        const expiresAt = Date.now() + ttl;
        const cacheData = { value, expiresAt, cachedAt: Date.now(), username: useUsername ? this.getCurrentUsername() : null };
        localStorage.setItem(fullKey, JSON.stringify(cacheData));
        localStorage.setItem(`${fullKey}_timestamp`, expiresAt.toString());
        return true;
      } catch (error2) {
        this.evictExpiredAndLargest();
        try {
          const fullKey = this.buildKey(key, useUsername);
          const expiresAt = Date.now() + ttl;
          const cacheData = { value, expiresAt, cachedAt: Date.now(), username: useUsername ? this.getCurrentUsername() : null };
          localStorage.setItem(fullKey, JSON.stringify(cacheData));
          localStorage.setItem(`${fullKey}_timestamp`, expiresAt.toString());
          return true;
        } catch (error3) {
          console.error('❌ Cache yazılamadı:', error3);
          return false;
        }
      }
    }
  }

  /**
   * Cache'den veri al
   * @param {string} key - Cache anahtarı
   * @param {boolean} useUsername - Username prefix kullan mı?
   * @returns {any|null} - Cache'deki veri veya null
   */
  get(key, useUsername = true) {
    try {
      const fullKey = this.buildKey(key, useUsername);
      const cached = localStorage.getItem(fullKey);
      
      if (!cached) return null;

      const cacheData = JSON.parse(cached);
      
      // TTL kontrolü
      if (Date.now() > cacheData.expiresAt) {
        // Süresi dolmuş, temizle
        this.remove(key, useUsername);
        return null;
      }

      // Username kontrolü (güvenlik için)
      if (useUsername) {
        const currentUsername = this.getCurrentUsername();
        if (cacheData.username && cacheData.username !== currentUsername) {
          // Farklı kullanıcının cache'i, temizle
          this.remove(key, useUsername);
          return null;
        }
      }

      return cacheData.value;
    } catch (error) {
      console.error('Cache okuma hatası:', error);
      return null;
    }
  }

  /**
   * Cache'den veri sil
   */
  remove(key, useUsername = true) {
    try {
      const fullKey = this.buildKey(key, useUsername);
      localStorage.removeItem(fullKey);
      localStorage.removeItem(`${fullKey}_timestamp`);
    } catch (error) {
      console.error('Cache silme hatası:', error);
    }
  }

  /**
   * Pattern'e uyan tüm cache'leri temizle
   * @param {string} pattern - Aranacak pattern (örn: "vod_movies" tüm film cache'lerini temizler)
   */
  invalidate(pattern) {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.prefix) && key.includes(pattern)) {
          localStorage.removeItem(key);
        }
      });
      console.log(`✅ "${pattern}" pattern'ine uyan cache'ler temizlendi`);
    } catch (error) {
      console.error('Cache invalidate hatası:', error);
    }
  }

  /**
   * Kullanıcı değiştiğinde tüm kullanıcı cache'lerini temizle
   */
  clearUserCache(username = null) {
    try {
      const targetUsername = username || this.getCurrentUsername();
      const keys = Object.keys(localStorage);
      
      let count = 0;
      keys.forEach(key => {
        if (key.startsWith(this.prefix) && key.includes(`_${targetUsername}_`)) {
          localStorage.removeItem(key);
          count++;
        }
      });
      
      console.log(`✅ ${targetUsername} kullanıcısının ${count} cache'i temizlendi`);
      return count;
    } catch (error) {
      console.error('User cache temizleme hatası:', error);
      return 0;
    }
  }

  /**
   * Tüm Tivigo cache'lerini temizle
   */
  clearAll() {
    try {
      const keys = Object.keys(localStorage);
      let count = 0;
      
      keys.forEach(key => {
        if (key.startsWith(this.prefix)) {
          localStorage.removeItem(key);
          count++;
        }
      });
      
      // Eski cache sisteminden kalanları da temizle
      const legacyKeys = [
        'webtv_channels_cache',
        'webtv_channels_timestamp',
        'vod_categories_cache',
        'vod_categories_timestamp',
        'series_categories_cache',
        'series_categories_timestamp'
      ];
      
      legacyKeys.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
          count++;
        }
      });
      
      // Pattern bazlı eski cache'ler
      const patterns = ['vod_movies_category_', 'vod_movies_timestamp_', 'series_category_', 'series_timestamp_'];
      keys.forEach(key => {
        patterns.forEach(pattern => {
          if (key.startsWith(pattern)) {
            localStorage.removeItem(key);
            count++;
          }
        });
      });
      
      console.log(`✅ Tüm cache temizlendi (${count} adet)`);
      return count;
    } catch (error) {
      console.error('Cache temizleme hatası:', error);
      return 0;
    }
  }

  /**
   * LRU Cache - Belirli bir kategoriye ait en eski cache'leri sil
   * @param {string} categoryType - 'vod_movies' veya 'series'
   * @param {number} maxCategories - Maksimum tutulacak kategori sayısı
   */
  applyLRU(categoryType, maxCategories = 5) {
    try {
      const username = this.getCurrentUsername();
      const pattern = `${this.prefix}${username}_${categoryType}_category_`;
      
      // Bu kullanıcının bu tipteki tüm kategori cache'lerini bul
      const keys = Object.keys(localStorage);
      const categoryKeys = keys.filter(key => key.startsWith(pattern));
      
      if (categoryKeys.length <= maxCategories) {
        return; // Limit aşılmamış, temizleme gerekmiyor
      }

      // Timestamp'lere göre sırala (en eski önce)
      const keysWithTime = categoryKeys.map(key => {
        const timestampKey = `${key}_timestamp`;
        const timestamp = parseInt(localStorage.getItem(timestampKey) || '0');
        return { key, timestamp };
      }).sort((a, b) => a.timestamp - b.timestamp);

      // En eski olanları sil (max limiti aşan kadarını)
      const toDelete = keysWithTime.slice(0, categoryKeys.length - maxCategories);
      
      toDelete.forEach(item => {
        localStorage.removeItem(item.key);
        localStorage.removeItem(`${item.key}_timestamp`);
      });

      console.log(`✅ LRU: ${toDelete.length} eski ${categoryType} kategori cache'i temizlendi`);
      return toDelete.length;
    } catch (error) {
      console.error('LRU temizleme hatası:', error);
      return 0;
    }
  }

  /**
   * Belirli bir pattern için en eski cache'leri temizle (LRU)
   * @param {string} pattern - Cache pattern'i
   * @param {number} maxCount - Maksimum tutulacak cache sayısı
   */
  cleanup(pattern, maxCount = 5) {
    try {
      const keys = Object.keys(localStorage);
      const matchingKeys = keys.filter(key => 
        key.startsWith(this.prefix) && key.includes(pattern)
      );

      if (matchingKeys.length <= maxCount) {
        return;
      }

      // Timestamp'lere göre sırala
      const keysWithTime = matchingKeys.map(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}');
          return { key, time: data.cachedAt || 0 };
        } catch {
          return { key, time: 0 };
        }
      }).sort((a, b) => a.time - b.time);

      // En eski olanları sil
      const toDelete = keysWithTime.slice(0, matchingKeys.length - maxCount);
      toDelete.forEach(item => {
        localStorage.removeItem(item.key);
      });

      console.log(`✅ ${toDelete.length} eski cache temizlendi`);
    } catch (error) {
      console.error('Cleanup hatası:', error);
    }
  }

  /**
   * Süresi dolmuş cache'leri sil, hâlâ yer yoksa en büyük entry'leri sil
   */
  evictExpiredAndLargest() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.prefix) && !key.endsWith('_timestamp'));
      const now = Date.now();

      // Önce süresi dolmuş olanları sil
      cacheKeys.forEach(key => {
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}');
          if (data.expiresAt && data.expiresAt < now) {
            localStorage.removeItem(key);
            localStorage.removeItem(`${key}_timestamp`);
          }
        } catch { /* skip */ }
      });

      // Hâlâ yer gerekiyorsa en büyük 3 entry'yi sil
      const remaining = Object.keys(localStorage)
        .filter(key => key.startsWith(this.prefix) && !key.endsWith('_timestamp'))
        .map(key => ({ key, size: (localStorage.getItem(key) || '').length }))
        .sort((a, b) => b.size - a.size);

      const toRemove = remaining.slice(0, 3);
      toRemove.forEach(item => {
        localStorage.removeItem(item.key);
        localStorage.removeItem(`${item.key}_timestamp`);
      });
    } catch (error) {
      console.error('Evict hatası:', error);
    }
  }

  /**
   * Cache istatistiklerini al
   */
  getStats() {
    try {
      const keys = Object.keys(localStorage);
      const cacheKeys = keys.filter(key => key.startsWith(this.prefix));
      
      let totalSize = 0;
      const stats = {
        totalItems: cacheKeys.length,
        byType: {},
        totalSizeKB: 0
      };

      cacheKeys.forEach(key => {
        const value = localStorage.getItem(key) || '';
        totalSize += value.length;
        
        // Tip belirle
        if (key.includes('webtv')) stats.byType.webtv = (stats.byType.webtv || 0) + 1;
        else if (key.includes('vod_movies')) stats.byType.movies = (stats.byType.movies || 0) + 1;
        else if (key.includes('series')) stats.byType.series = (stats.byType.series || 0) + 1;
        else stats.byType.other = (stats.byType.other || 0) + 1;
      });

      stats.totalSizeKB = Math.round(totalSize / 1024);
      
      return stats;
    } catch (error) {
      console.error('Stats hatası:', error);
      return { totalItems: 0, byType: {}, totalSizeKB: 0 };
    }
  }
}

// Singleton instance
export const cacheManager = new CacheManager();

// Kullanım örnekleri:
// cacheManager.set('webtv_channels', data, 24*60*60*1000); // 24 saat TTL
// const data = cacheManager.get('webtv_channels');
// cacheManager.invalidate('vod_movies'); // Tüm film cache'lerini temizle
// cacheManager.clearUserCache(); // Mevcut kullanıcının tüm cache'lerini temizle
// cacheManager.applyLRU('vod_movies', 5); // Max 5 film kategorisi tut

export default cacheManager;

